import { Link } from 'react-router-dom';
import { Lightning } from '@phosphor-icons/react';

import { MoneyDisplay } from '../../common/MoneyDisplay';
import { Button } from '../../ui/Button';
import { Slider } from '../../ui/Slider';
import { WizardStepHeader } from '../../wizard';
import { paths } from '../../../routes/paths';
import pt from '../../../locales/pt';
import {
  formatPayoffDatePt,
  targetDateFromPayoffMonths,
} from '../../../utils/debtPlanUtils';
import { formatFreeEta } from '../../../lib/debtDisplayUtils';
import { cn } from '../../../utils/cn';

function freeDateLabel(simulation) {
  if (!simulation || simulation.requires_more_data) return '—';
  if (simulation.predicted_payoff_date) {
    return (
      formatFreeEta(simulation.predicted_payoff_date) ||
      formatPayoffDatePt(simulation.predicted_payoff_date)
    );
  }
  const months = simulation.accelerated_payoff_months;
  if (months == null) return '—';
  return formatFreeEta(months) || formatPayoffDatePt(targetDateFromPayoffMonths(months));
}

/**
 * Slider motivacional — extra mensal com resultado em tempo real.
 */
export function DebtSimulateStep({
  extra,
  onExtraChange,
  suggestedHalf,
  onUseHalf,
  simulation,
  simulating,
  sliderMax = 500,
}) {
  const copy = pt.liabilities.repaymentWizard.simulate;
  const monthsSaved = Number(simulation?.months_saved) || 0;
  const interestSaved = Number(simulation?.interest_saved_eur) || 0;
  const hasGain = monthsSaved > 0;
  const monthsWord =
    monthsSaved === 1 ? copy.monthsWordOne : copy.monthsWordMany;
  const freeDate = freeDateLabel(simulation);
  const extraFmt = Math.round(Number(extra) || 0).toLocaleString('pt-PT');

  return (
    <div className="flex flex-col gap-4 pt-1.5">
      <WizardStepHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
      />

      <div
        className={cn(
          'rounded-[20px] bg-gradient-to-br from-sage-600 to-sage-800 px-[18px] py-[18px] text-center',
          'shadow-[0_16px_30px_-16px_rgba(74,98,67,0.7)]',
        )}
      >
        <p className="text-xs font-semibold text-sage-200">
          {copy.extraPerMonth.replace('{amount}', extraFmt)}
        </p>
        <p className="mt-1 font-display text-[30px] font-bold leading-tight tracking-tight text-white">
          {copy.freeIn.replace('{date}', freeDate)}
        </p>
        {simulating ? (
          <p className="mt-3 text-[12.5px] text-sage-200">{pt.common.loading}</p>
        ) : simulation?.requires_more_data ? (
          <div className="mt-3 space-y-2">
            <p className="text-[12.5px] text-sage-100">{copy.requiresData}</p>
            <Button asChild variant="secondary" size="sm">
              <Link to={paths.dividas}>{copy.editDebt}</Link>
            </Button>
          </div>
        ) : hasGain ? (
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <span className="rounded-[11px] bg-white/15 px-2.5 py-1 text-xs font-semibold text-white">
              {copy.monthsEarlier
                .replace('{n}', String(monthsSaved))
                .replace('{monthsWord}', monthsWord)}
            </span>
            <span className="rounded-[11px] bg-white/15 px-2.5 py-1 text-xs font-semibold text-white">
              {copy.interestSavedChip.replace(
                '{amount}',
                Math.round(interestSaved).toLocaleString('pt-PT'),
              )}
            </span>
          </div>
        ) : (
          <p className="mt-3 text-[12.5px] text-sage-200">{copy.baselineHint}</p>
        )}
      </div>

      <div>
        <Slider
          min={0}
          max={sliderMax}
          step={10}
          value={[Math.min(sliderMax, Math.max(0, Number(extra) || 0))]}
          onValueChange={(vals) => onExtraChange(vals[0] ?? 0)}
          aria-label={copy.title}
        />
        <div className="mt-2 flex justify-between text-[11px] font-semibold text-muted-foreground">
          <span>0 €</span>
          <span>{Math.round(sliderMax / 2).toLocaleString('pt-PT')} €</span>
          <span>{sliderMax.toLocaleString('pt-PT')} €</span>
        </div>
      </div>

      {suggestedHalf > 0 ? (
        <button
          type="button"
          onClick={onUseHalf}
          className="flex w-full items-center justify-between gap-2.5 rounded-[14px] border border-dashed border-sage-300 bg-sage-50/80 px-[15px] py-3 text-left dark:border-sage-700 dark:bg-sage-950/30"
        >
          <span className="flex items-center gap-2.5">
            <Lightning
              className="h-[18px] w-[18px] shrink-0 text-sage-600 dark:text-sage-400"
              weight="regular"
              aria-hidden
            />
            <span className="text-[13px] font-semibold text-foreground">
              {copy.halfSuggestion}
            </span>
          </span>
          <span className="font-display text-sm font-bold text-sage-600 dark:text-sage-400">
            <MoneyDisplay value={suggestedHalf} size="sm" />
          </span>
        </button>
      ) : null}
    </div>
  );
}

export default DebtSimulateStep;
